import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { listApplications } from '../api/admin'
import type { AdminApplication, AdminPagination } from '../api/adminTypes'
import { Card } from '../components/ui/Card'
import { StatusBadge } from './StatusBadge'
import { formatDateTime } from './format'
import { applicationReference } from '../screens/MyApplicationsScreen'
import { LoadingNote, ErrorNote, EmptyNote } from './Notes'
import { Pager } from './Pager'

type StatusFilter = 'all' | 'in_progress' | 'submitted'

export function ApplicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filter = (searchParams.get('status') as StatusFilter) || 'all'
  const [applications, setApplications] = useState<AdminApplication[] | null>(null)
  const [pagination, setPagination] = useState<AdminPagination | null>(null)
  const [offset, setOffset] = useState(0)
  const [error, setError] = useState(false)

  // Switching filters starts over at page 1 — otherwise a page 3 offset from
  // "All" could land past the end of a smaller "Drafts" result set.
  useEffect(() => {
    setOffset(0)
  }, [filter])

  useEffect(() => {
    setApplications(null)
    setError(false)
    listApplications(filter === 'all' ? undefined : filter, offset)
      .then((res) => {
        setApplications(res.applications)
        setPagination(res.pagination)
      })
      .catch(() => setError(true))
  }, [filter, offset])

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-6)' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-lg)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          Applications
        </h1>
        <FilterTabs
          value={filter}
          onChange={(v) => setSearchParams(v === 'all' ? {} : { status: v })}
        />
      </div>

      {error && <ErrorNote>Something went wrong loading applications.</ErrorNote>}
      {applications === null && !error && <LoadingNote>Loading applications…</LoadingNote>}
      {applications !== null && applications.length === 0 && <EmptyNote>No applications match this filter.</EmptyNote>}

      {applications !== null && applications.length > 0 && (
        <>
          <Card padding={false} style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-outline-variant)' }}>
                  <Th>Reference</Th>
                  <Th>Account</Th>
                  <Th>Form</Th>
                  <Th>Status</Th>
                  <Th>SF state</Th>
                  <Th>Attempts</Th>
                  <Th>Last updated</Th>
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--color-outline-subtle)' }}>
                    <Td>{applicationReference(a.id)}</Td>
                    <Td>
                      <Link
                        to={`/applications/${a.id}`}
                        style={{ color: 'var(--color-action-primary)', textDecoration: 'none', fontWeight: 'var(--weight-semibold)' }}
                      >
                        {a.account_email}
                      </Link>
                    </Td>
                    <Td>{a.form_type.toUpperCase()}</Td>
                    <Td><StatusBadge status={a.status} /></Td>
                    <Td>{a.sf_state ? <StatusBadge status={a.sf_state} /> : '—'}</Td>
                    <Td>{a.attempts_count}</Td>
                    <Td>{formatDateTime(a.last_updated_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          {pagination && <Pager pagination={pagination} onOffsetChange={setOffset} />}
        </>
      )}
    </div>
  )
}

function FilterTabs({ value, onChange }: { value: StatusFilter; onChange: (v: StatusFilter) => void }) {
  const options: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'in_progress', label: 'Drafts' },
    { value: 'submitted', label: 'Submissions' },
  ]
  return (
    <div className="flex items-center gap-1">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          style={{
            padding: '8px 14px',
            borderRadius: 'var(--radius-full)',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--weight-semibold)',
            color: value === o.value ? 'var(--color-text-on-primary)' : 'var(--color-text-secondary)',
            background: value === o.value ? 'var(--color-action-primary)' : 'var(--color-surface-container-low)',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{ padding: '10px 16px', fontSize: 'var(--text-label-sm)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-secondary)' }}>
      {children}
    </th>
  )
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: '12px 16px', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-primary)' }}>
      {children}
    </td>
  )
}
